import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import {
  getDefaultWorkspaceDashboardRange,
  getDefaultWorkspaceRange,
  type SnapshotRangeState,
} from '@/components/snapshot/SnapshotDateRangePicker';
import { loadAppDateRange, saveAppDateRange } from '@/api/appDateRangeStorage';
import { getPriorPeriod, type DateRange } from '@/api/reportDateRanges';

const WORKSPACE_DASHBOARD_PATH =
  /^\/accounts\/[^/]+\/campaigns\/(workspace|dashboard|list)\/?$/;
const BRAND_DASHBOARD_PATH = /^\/brands\/[^/]+\/campaigns\/dashboard\/?$/;

export function isWorkspaceOrDashboardCampaignPath(pathname: string): boolean {
  return WORKSPACE_DASHBOARD_PATH.test(pathname) || BRAND_DASHBOARD_PATH.test(pathname);
}

export function resolveInitialAppDateRange(pathname: string): SnapshotRangeState {
  const stored = loadAppDateRange();
  if (stored) return stored;
  if (isWorkspaceOrDashboardCampaignPath(pathname)) {
    return getDefaultWorkspaceDashboardRange();
  }
  return getDefaultWorkspaceRange();
}

export function resolveEffectiveAppDateRange(
  pathname: string,
  range: SnapshotRangeState,
  hasStoredRange: boolean
): SnapshotRangeState {
  if (hasStoredRange) return range;
  if (isWorkspaceOrDashboardCampaignPath(pathname)) {
    return getDefaultWorkspaceDashboardRange();
  }
  return range;
}

export type AppDateRangePickerMode = 'compare' | 'simple' | 'current-only' | null;

type AppDateRangeContextValue = {
  range: SnapshotRangeState;
  setRange: (next: SnapshotRangeState) => void;
  setCurrentRange: (current: DateRange) => void;
};

const AppDateRangeContext = createContext<AppDateRangeContextValue | null>(null);

const SIMPLE_RANGE_PATH =
  /^\/accounts\/[^/]+\/campaigns\/(stream-v2|stream|budget-usage|retail)\/?$/;
const SOV_PATH = /^\/accounts\/[^/]+\/campaigns\/sov(?:\/|$)/;
const N_WORDS_PATH = /^\/accounts\/[^/]+\/campaigns\/n-words\/?$/;
/** Flipkart Workspace only — dashboard keeps page-local controls / no TopBar picker. */
const FLIPKART_WORKSPACE_PATH =
  /^\/flipkart\/[^/]+\/(?:accounts\/[^/]+\/)?campaigns\/workspace\/?$/;

export function isShareOfVoicePath(pathname: string): boolean {
  return SOV_PATH.test(pathname);
}

export function isFlipkartWorkspacePath(pathname: string): boolean {
  return FLIPKART_WORKSPACE_PATH.test(pathname);
}

export function getAppDateRangePickerMode(pathname: string): AppDateRangePickerMode {
  if (isWorkspaceOrDashboardCampaignPath(pathname) || isFlipkartWorkspacePath(pathname)) {
    return 'compare';
  }
  if (SIMPLE_RANGE_PATH.test(pathname) || SOV_PATH.test(pathname) || N_WORDS_PATH.test(pathname)) {
    return 'simple';
  }
  if (/^\/accounts\/[^/]+\/campaigns\/recommendations\/?$/.test(pathname)) return 'current-only';
  return null;
}

const DAYPARTING_PATH = /^\/accounts\/[^/]+\/campaigns\/stream(?:-v2)?\/?$/;
const RETAIL_PATH = /^\/accounts\/[^/]+\/campaigns\/retail\/?$/;

/** Top date pickers on Dashboard, Workspace, Dayparting, and Vendor cannot select after today. */
export function shouldDisableFutureDatesInTopPicker(pathname: string): boolean {
  return (
    isWorkspaceOrDashboardCampaignPath(pathname) ||
    isFlipkartWorkspacePath(pathname) ||
    DAYPARTING_PATH.test(pathname) ||
    RETAIL_PATH.test(pathname) ||
    SOV_PATH.test(pathname) ||
    N_WORDS_PATH.test(pathname)
  );
}

export function AppDateRangeProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [range, setRangeState] = useState<SnapshotRangeState>(() =>
    resolveInitialAppDateRange(typeof window !== 'undefined' ? window.location.pathname : '')
  );

  const effectiveRange = useMemo(() => {
    const hasStoredRange = loadAppDateRange() !== null;
    return resolveEffectiveAppDateRange(location.pathname, range, hasStoredRange);
  }, [range, location.pathname]);

  const setRange = useCallback((next: SnapshotRangeState) => {
    setRangeState(next);
    saveAppDateRange(next);
  }, []);

  const setCurrentRange = useCallback((current: DateRange) => {
    setRangeState((prev) => {
      const next: SnapshotRangeState = {
        current,
        previous: getPriorPeriod(current),
        compareEnabled: prev.compareEnabled,
      };
      saveAppDateRange(next);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ range: effectiveRange, setRange, setCurrentRange }),
    [effectiveRange, setRange, setCurrentRange]
  );

  return <AppDateRangeContext.Provider value={value}>{children}</AppDateRangeContext.Provider>;
}

export function useAppDateRange() {
  const ctx = useContext(AppDateRangeContext);
  if (!ctx) {
    throw new Error('useAppDateRange must be used within AppDateRangeProvider');
  }
  return ctx;
}
